import { ref, watch, onMounted } from '@nuxtjs/composition-api';

/**
 * Preload-and-mount strategy for sliders/galleries that avoids refetching images:
 * - Only mounts the active slide (and optional neighbors) initially → no "load everything" burst.
 * - Warms image URLs for neighbors via JS Image() (not DOM) → instant show when mounted.
 * - Once a slide is mounted, it stays mounted → no re-requests when revisiting.
 *
 * Works with:
 *   - simple URLs (string),
 *   - objects with {src, srcset, url, href},
 *   - arrays of <source> descriptors for <picture> (e.g., [{ type, srcset }, { src }]).
 *
 * @param {Object}   opts
 * @param {Array}    opts.items             Your data array (images, slides, etc.)
 * @param {Function} opts.getSrc            (item, index) => string | object | array (see shapes above)
 * @param {Ref}      opts.activeIndex       ref(Number) pointing at the current slide index
 * @param {Number}   [opts.preloadRadius=1] How many neighbors to warm/mount (1 = prev/next)
 * @param {Boolean}  [opts.lookAhead=true]  Also warm one extra slide beyond the radius in the forward direction
 *
 * @returns {{
 *   mounted: Ref<boolean[]>,
 *   loaded: Ref<boolean[]>,
 *   isMounted: (i:number)=>boolean,
 *   imgBind: (i:number)=>{loading:string, fetchpriority:string}
 * }}
 */

export function useSlidePreloader({ items, getSrc, activeIndex, preloadRadius = 1, lookAhead = true }) {
  const n = items.length;
  const mounted = ref(Array(n).fill(false)); // controls v-if (mounted in DOM yet?)
  const loaded = ref(Array(n).fill(false)); // have we warmed this index's URLs yet?
  const warmed = new Set(); // de-dupe warmed URLs across indices

  const inBounds = (i) => i >= 0 && i < n;

  // --- URL extraction helpers -------------------------------------------------

  // Turn a srcset string into an array of real URLs
  const splitSrcset = (s) =>
    String(s)
      .split(',')
      .map((x) => x.trim().split(/\s+/)[0]) // grab URL before "560w"/"2x"
      .filter(Boolean);

  // Normalize any of: string | object | array → string[] (URL list)
  const toUrls = (src) => {
    if (!src) return [];

    // string: either a single URL or a full srcset
    if (typeof src === 'string') {
      return src.includes(',') || /\s\d+w|\s\d+x/.test(src) ? splitSrcset(src) : [src.trim().split(/\s+/)[0]];
    }

    // array: map each element recursively (supports <picture> sources arrays)
    if (Array.isArray(src)) {
      return src.flatMap(toUrls);
    }

    // object: common shapes { src, srcset, url, href }
    if (typeof src === 'object') {
      const out = [];
      if (src.src) out.push(src.src);
      if (src.srcset) out.push(...splitSrcset(src.srcset));
      if (src.url) out.push(src.url);
      if (src.href) out.push(src.href);
      return out;
    }

    return [];
  };

  // --- Warm / mount primitives ------------------------------------------------

  // Warm a single URL (client-only). Does not touch the DOM.
  const warmOne = (url) => {
    if (!process.client || !url || warmed.has(url)) return;
    warmed.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = url;
  };

  // Warm all URLs for an index once
  const warmIndex = (i) => {
    if (!inBounds(i) || loaded.value[i]) return;
    toUrls(getSrc(items[i], i)).forEach(warmOne);
    loaded.value[i] = true;
  };

  // Allow a slide to exist in the DOM (v-if gate)
  const mountIndex = (i) => {
    if (inBounds(i)) mounted.value[i] = true;
  };

  // Ensure "window" around the active index is warmed and mounted
  const ensureWindow = (center) => {
    // Warm + mount the active first for immediate paint (esp. on mobile)
    warmIndex(center);
    mountIndex(center);

    // Warm neighbors
    for (let d = 1; d <= preloadRadius; d++) {
      warmIndex(center - d);
      warmIndex(center + d);
    }

    // Then mount neighbors (so they render from cache without layout jank)
    for (let d = 1; d <= preloadRadius; d++) {
      mountIndex(center - d);
      mountIndex(center + d);
    }

    // Optional extra look-ahead
    if (lookAhead) {
      warmIndex(center + preloadRadius + 1);
    }
  };

  // Run once immediately so the active slide is available for first paint
  ensureWindow(activeIndex.value);

  // Re-run after mount (harmless; covers layout-dependent srcset decisions)
  onMounted(() => ensureWindow(activeIndex.value));

  // Update when the active slide changes
  watch(activeIndex, (i) => ensureWindow(i));

  // --- Template helpers -------------------------------------------------------

  // Whether a given slide should be rendered (v-if)
  const isMounted = (i) => !!mounted.value[i];

  // Attributes you can bind onto your <img>/<Image> (if forwarded)
  const imgBind = (i) => ({
    // Active slide should not be lazily deferred
    loading: i === activeIndex.value ? 'eager' : 'lazy',
    // Hint to the browser's fetch scheduler
    fetchpriority: i === activeIndex.value ? 'high' : Math.abs(i - activeIndex.value) <= preloadRadius ? 'low' : 'auto',
  });

  return { mounted, loaded, isMounted, imgBind };
}
